import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, X, Loader2, Users, Upload, Image, Edit, Check } from "lucide-react";
import { getImageUrl, DEFAULT_PLAYER_IMAGE } from "@/lib/imageUtils";

const PlayersManager = () => {
  const { authAxios } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    position: "QB",
    height: "",
    weight: "",
    school: "",
    jersey_number: "",
    image_url: ""
  });

  const positions = ["QB", "RB", "WR", "TE", "OL", "DL", "LB", "CB", "S", "K", "P"];

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players`);
      setPlayers(response.data);
    } catch (e) {
      toast.error("Failed to fetch players");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'players');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
      
      if (isEdit && editingPlayer) {
        setEditingPlayer({ ...editingPlayer, image_url: fullUrl });
      } else {
        setFormData({ ...formData, image_url: fullUrl });
      }
      toast.success("Photo uploaded!");
    } catch (e) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (editFileInputRef.current) editFileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        jersey_number: formData.jersey_number ? parseInt(formData.jersey_number) : null
      };
      
      await authAxios.post('/players', payload);
      toast.success("Player added successfully!");
      setShowForm(false);
      resetForm();
      fetchPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add player");
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer({
      ...player,
      jersey_number: player.jersey_number || ""
    });
    setShowForm(false);
  };

  const handleSaveEdit = async () => {
    if (!editingPlayer) return;
    
    try {
      const payload = {
        name: editingPlayer.name,
        position: editingPlayer.position,
        height: editingPlayer.height,
        weight: editingPlayer.weight,
        school: editingPlayer.school,
        jersey_number: editingPlayer.jersey_number ? parseInt(editingPlayer.jersey_number) : null,
        image_url: editingPlayer.image_url || null
      };
      
      await authAxios.put(`/players/${editingPlayer.id}`, payload);
      toast.success("Player updated successfully!");
      setEditingPlayer(null);
      fetchPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update player");
    }
  };

  const handleCancelEdit = () => {
    setEditingPlayer(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this player?")) return;
    
    try {
      await authAxios.delete(`/players/${id}`);
      toast.success("Player deleted");
      if (editingPlayer?.id === id) setEditingPlayer(null);
      fetchPlayers();
    } catch (e) {
      toast.error("Failed to delete player");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      position: "QB",
      height: "",
      weight: "",
      school: "",
      jersey_number: "",
      image_url: ""
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-neon-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-neon-blue" />
          Manage Players ({players.length})
        </h2>
        <button
          onClick={() => { setShowForm(!showForm); setEditingPlayer(null); }}
          data-testid="add-player-btn"
          className="cyber-btn py-2 px-4 text-xs"
        >
          <span className="flex items-center gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "CANCEL" : "ADD PLAYER"}
          </span>
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="cyber-card p-6 space-y-4" data-testid="player-add-form">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-neon-blue/30">
            <Plus className="w-5 h-5 text-neon-blue" />
            <span className="font-orbitron text-sm text-neon-blue">ADD NEW PLAYER</span>
          </div>
          
          {/* Photo Upload Section */}
          <div className="border border-dashed border-neon-blue/50 p-4 mb-4">
            <Label className="font-orbitron text-xs text-neon-blue mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              PLAYER PHOTO
            </Label>
            
            <div className="flex items-start gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handlePhotoUpload(e, false)}
                className="hidden"
                id="player-photo-upload"
              />
              
              {/* Clickable Photo Preview Area */}
              <label
                htmlFor="player-photo-upload"
                className="w-32 h-32 bg-white/5 border-2 border-dashed border-neon-blue/30 hover:border-neon-blue/60 flex flex-col items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer transition-all hover:bg-neon-blue/5 group"
              >
                {formData.image_url ? (
                  <div className="relative w-full h-full">
                    <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Upload className="w-6 h-6 text-neon-blue" />
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-500 group-hover:text-neon-blue transition-colors" />
                    <span className="font-rajdhani text-[10px] text-gray-500 group-hover:text-neon-blue mt-1">Click to upload</span>
                  </>
                )}
              </label>

              <div className="flex-1 space-y-2">
                <label
                  htmlFor="player-photo-upload"
                  data-testid="player-upload-btn"
                  className={`cyber-btn py-3 px-6 text-sm cursor-pointer inline-flex items-center gap-2 border-neon-blue text-neon-blue hover:bg-neon-blue hover:text-black ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      UPLOADING...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      UPLOAD PHOTO
                    </>
                  )}
                </label>
                <p className="font-rajdhani text-xs text-gray-400 mt-2">
                  Supported: JPG, PNG, GIF, WebP (max 10MB)
                </p>
                <p className="font-rajdhani text-xs text-gray-500">Or paste image URL below</p>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="https://..."
                  className="bg-void-black border-white/10 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="Player name"
                className="bg-void-black border-white/10"
                data-testid="player-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Position *</Label>
              <Select value={formData.position} onValueChange={(v) => setFormData({...formData, position: v})}>
                <SelectTrigger className="bg-void-black border-white/10" data-testid="player-position-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-void-black border-white/10">
                  {positions.map(pos => (
                    <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Jersey #</Label>
              <Input
                type="number"
                value={formData.jersey_number}
                onChange={(e) => setFormData({...formData, jersey_number: e.target.value})}
                placeholder="00"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Height *</Label>
              <Input
                value={formData.height}
                onChange={(e) => setFormData({...formData, height: e.target.value})}
                required
                placeholder="6'2&quot;"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Weight *</Label>
              <Input
                value={formData.weight}
                onChange={(e) => setFormData({...formData, weight: e.target.value})}
                required
                placeholder="215 lbs"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">School *</Label>
              <Input
                value={formData.school}
                onChange={(e) => setFormData({...formData, school: e.target.value})}
                required
                placeholder="School name"
                className="bg-void-black border-white/10"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" className="cyber-btn py-2 px-6 text-xs" data-testid="save-player-btn">
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                SAVE PLAYER
              </span>
            </button>
          </div>
        </form>
      )}

      {/* Players List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {players.map(player => (
          <div key={player.id} className={`cyber-card p-4 group ${editingPlayer?.id === player.id ? 'ring-2 ring-neon-green' : ''}`} data-testid={`player-card-${player.id}`}>
            {editingPlayer?.id === player.id ? (
              /* Edit Mode */
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-neon-green/30">
                  <Edit className="w-4 h-4 text-neon-green" />
                  <span className="font-orbitron text-xs text-neon-green">EDITING PLAYER</span>
                </div>
                
                {/* Edit Photo */}
                <div className="space-y-2">
                  <Label className="font-orbitron text-[10px] text-neon-green flex items-center gap-1">
                    <Upload className="w-3 h-3" />
                    PLAYER PHOTO
                  </Label>
                  <div className="flex items-center gap-4">
                    <input
                      ref={editFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, true)}
                      className="hidden"
                      id={`edit-player-photo-${player.id}`}
                    />
                    
                    {/* Clickable Photo Preview */}
                    <label
                      htmlFor={`edit-player-photo-${player.id}`}
                      className="w-24 h-24 bg-white/5 border-2 border-dashed border-neon-green/30 hover:border-neon-green/60 flex flex-col items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer transition-all hover:bg-neon-green/5 group"
                    >
                      {editingPlayer.image_url ? (
                        <div className="relative w-full h-full">
                          <img src={getImageUrl(editingPlayer.image_url)} alt="Preview" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Upload className="w-5 h-5 text-neon-green" />
                          </div>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-gray-500 group-hover:text-neon-green transition-colors" />
                          <span className="font-rajdhani text-[9px] text-gray-500 group-hover:text-neon-green mt-1">Click</span>
                        </>
                      )}
                    </label>
                    
                    <div className="flex-1">
                      <label
                        htmlFor={`edit-player-photo-${player.id}`}
                        data-testid={`edit-upload-btn-${player.id}`}
                        className={`cyber-btn py-2 px-4 text-xs cursor-pointer inline-flex items-center gap-2 border-neon-green text-neon-green hover:bg-neon-green hover:text-black ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {uploading ? 'UPLOADING...' : 'CHANGE PHOTO'}
                      </label>
                      <Input
                        value={editingPlayer.image_url || ''}
                        onChange={(e) => setEditingPlayer({...editingPlayer, image_url: e.target.value})}
                        placeholder="Or paste URL..."
                        className="bg-void-black border-white/10 text-xs mt-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Edit Fields */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <Label className="font-orbitron text-[10px] text-gray-500">Name</Label>
                    <Input
                      value={editingPlayer.name}
                      onChange={(e) => setEditingPlayer({...editingPlayer, name: e.target.value})}
                      className="bg-void-black border-white/10 text-sm h-8"
                      data-testid={`edit-name-${player.id}`}
                    />
                  </div>
                  <div>
                    <Label className="font-orbitron text-[10px] text-gray-500">Position</Label>
                    <Select value={editingPlayer.position} onValueChange={(v) => setEditingPlayer({...editingPlayer, position: v})}>
                      <SelectTrigger className="bg-void-black border-white/10 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-void-black border-white/10">
                        {positions.map(pos => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-orbitron text-[10px] text-gray-500">Jersey #</Label>
                    <Input
                      type="number"
                      value={editingPlayer.jersey_number}
                      onChange={(e) => setEditingPlayer({...editingPlayer, jersey_number: e.target.value})}
                      className="bg-void-black border-white/10 text-sm h-8"
                    />
                  </div>
                  <div>
                    <Label className="font-orbitron text-[10px] text-gray-500">Height</Label>
                    <Input
                      value={editingPlayer.height}
                      onChange={(e) => setEditingPlayer({...editingPlayer, height: e.target.value})}
                      className="bg-void-black border-white/10 text-sm h-8"
                    />
                  </div>
                  <div>
                    <Label className="font-orbitron text-[10px] text-gray-500">Weight</Label>
                    <Input
                      value={editingPlayer.weight}
                      onChange={(e) => setEditingPlayer({...editingPlayer, weight: e.target.value})}
                      className="bg-void-black border-white/10 text-sm h-8"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="font-orbitron text-[10px] text-gray-500">School</Label>
                    <Input
                      value={editingPlayer.school}
                      onChange={(e) => setEditingPlayer({...editingPlayer, school: e.target.value})}
                      className="bg-void-black border-white/10 text-sm h-8"
                    />
                  </div>
                </div>

                {/* Edit Actions */}
                <div className="flex justify-between pt-2 border-t border-white/10">
                  <button
                    onClick={handleCancelEdit}
                    className="text-gray-500 hover:text-white transition-colors p-2 flex items-center gap-1 text-xs font-rajdhani"
                    data-testid={`cancel-edit-${player.id}`}
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="cyber-btn py-2 px-4 text-xs border-neon-green text-neon-green hover:bg-neon-green hover:text-black"
                    data-testid={`save-edit-${player.id}`}
                  >
                    <span className="flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      SAVE CHANGES
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                <div className="flex gap-4">
                  <div className="w-16 h-16 bg-white/5 flex-shrink-0 overflow-hidden">
                    {player.image_url ? (
                      <img 
                        src={getImageUrl(player.image_url)} 
                        alt={player.name} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_PLAYER_IMAGE;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600">
                        <Users className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-orbitron text-sm text-white truncate">{player.name}</h3>
                        <p className="font-rajdhani text-xs text-gray-500">{player.school}</p>
                      </div>
                      <span className="font-orbitron text-lg text-neon-blue">{player.position}</span>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs font-rajdhani text-gray-400">
                      <span>{player.height}</span>
                      <span>{player.weight}</span>
                      {player.jersey_number && <span>#{player.jersey_number}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-white/5">
                  <button
                    onClick={() => handleEdit(player)}
                    className="text-gray-500 hover:text-neon-green transition-colors p-2 hover:bg-neon-green/10 rounded"
                    title="Edit player"
                    data-testid={`edit-player-${player.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(player.id)}
                    className="text-gray-500 hover:text-red-500 transition-colors p-2 hover:bg-red-500/10 rounded"
                    title="Delete player"
                    data-testid={`delete-player-${player.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {players.length === 0 && (
        <div className="text-center py-12 cyber-card" data-testid="empty-players-state">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No players yet. Add your first player!</p>
        </div>
      )}
    </div>
  );
};

export default PlayersManager;
